"use client";

import * as React from "react";
import Link from "next/link";
import {
  Crown,
  Mail,
  Send,
  Sparkles,
  ArrowUp,
  MapPin,
  Phone,
  Lock,
  Heart,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";

const FOOTER_NAV = [
  {
    title: "The Operator",
    links: [
      { label: "About Dr. Jyoti Kush", href: "/about/" },
      { label: "Biography", href: "/about/biography/" },
      { label: "Journey", href: "/about/journey/" },
      { label: "Credentials", href: "/about/credentials/" },
      { label: "Recognition", href: "/about/recognition/" },
    ],
  },
  {
    title: "Expertise",
    links: [
      { label: "All Expertise", href: "/expertise/" },
      { label: "Operational Excellence", href: "/expertise/operational-excellence/" },
      { label: "Strategic Intelligence", href: "/expertise/strategic-intelligence/" },
      { label: "Political Strategy", href: "/expertise/political-strategy/" },
      { label: "Governance", href: "/expertise/governance-public-policy/" },
    ],
  },
  {
    title: "Engagements",
    links: [
      { label: "Speaking", href: "/speaking/" },
      { label: "Services", href: "/services/" },
      { label: "The 70% Project", href: "/the-70-percent-project/" },
      { label: "Contact", href: "/contact/" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "The Operator&apos;s Notebook", href: "/the-operators-notebook/" },
      { label: "Insights", href: "/insights/" },
      { label: "FAQ", href: "/faq/" },
      { label: "Glossary", href: "/glossary/" },
      { label: "Sitemap", href: "/sitemap.xml" },
    ],
  },
];

const SOCIAL = [
  { label: "X (Twitter)", href: "https://x.com/jyotikush", initial: "X" },
  { label: "LinkedIn", href: "https://linkedin.com/in/jyotikush", initial: "in" },
  { label: "YouTube", href: "https://youtube.com/@jyotikush", initial: "YT" },
  { label: "GitHub", href: "https://github.com/jyotikush", initial: "GH" },
];

export function MainFooter() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const [email, setEmail] = React.useState("");
  const [subscribed, setSubscribed] = React.useState(false);

  const onSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.includes("@")) {
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 4000);
      setEmail("");
    }
  };

  return (
    <footer className="relative mt-20 border-t border-border/50 bg-gradient-to-b from-transparent to-muted/40">
      {/* Newsletter band */}
      <div className="border-b border-border/40 bg-gradient-hero">
        <div className="container-1720 py-12 lg:py-16">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-red mb-4">
                <BookOpen className="size-3.5 text-red" />
                <span className="text-overline text-red">The Operator&apos;s Notebook</span>
              </div>
              <h3 className="text-display-md mb-3">
                One principle. Every two weeks. <span className="text-gradient-royal">No filler.</span>


              </h3>
              <p className="text-body-md text-muted-foreground max-w-xl">
                Biweekly notes on how excellent things run. Drawn from a decade of sovereign-scale operations. Made usable for any organization.
              </p>
            </div>
            <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 h-12 rounded-lg glass border border-border/60 text-body-md focus:border-red focus:outline-none focus:ring-2 focus:ring-red/30 transition-all"
                />
              </div>
              <Button
                type="submit"
                size="lg"
                className="h-12 px-6 bg-gradient-sovereign hover:scale-105 transition-transform shadow-lg"
                disabled={subscribed}
              >
                {subscribed ? (
                  <>
                    <Sparkles className="size-4" />
                    Subscribed
                  </>
                ) : (
                  <>
                    <Send className="size-4" />
                    Subscribe
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="container-1720 py-14 lg:py-20">
        <div className="grid lg:grid-cols-[1.3fr_2.5fr] gap-10 lg:gap-16">
          {/* Brand block */}
          <div>
            <Link href="/" className="flex items-center gap-3 mb-5">
              <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-xl">
                <Crown className="size-6 text-white" />
              </div>
              <div>
                <div className="text-heading-md font-bold leading-none">Dr. Jyoti Kush</div>
                <div className="text-caption text-muted-foreground leading-none mt-1">COO · CryptoMize</div>
              </div>
            </Link>
            <p className="text-body-sm text-muted-foreground leading-relaxed mb-5 max-w-md">
              A physician who became an operator. Chief Operating Officer of CryptoMize and MaxiMize Infinium — the world&apos;s only full-spectrum Digital Conglomerate for Strategic Sovereignty.
            </p>
            <div className="space-y-2 text-caption text-muted-foreground mb-5">
              <div className="flex items-start gap-2">
                <MapPin className="size-3.5 mt-0.5 text-red shrink-0" />
                <span>New Delhi · 18 countries · 3 continents</span>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="size-3.5 mt-0.5 text-emerald shrink-0" />
                <span>By introduction only — absolute discretion</span>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="size-3.5 mt-0.5 text-cyan shrink-0" />
                <span>Zero security breaches. Fifteen-plus years.</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  // a11y: accessible name must contain the visible initial (LH label-content-name-mismatch)
                  className="size-9 rounded-md glass flex items-center justify-center text-caption font-bold text-muted-foreground hover:text-red hover:scale-110 transition-all"
                  aria-label={s.label + " — " + s.initial}
                >
                  {s.initial}
                </a>
              ))}
            </div>
          </div>

          {/* Nav columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {FOOTER_NAV.map((col) => (
              <div key={col.title}>
                <div className="text-overline text-foreground mb-4">{col.title}</div>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.href}>
                      <Link
                        href={l.href}
                        className="text-body-sm text-muted-foreground hover:text-red transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom band */}
      <div className="border-t border-border/40">
        <div className="container-1720 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-caption text-muted-foreground">
            <div className="flex flex-wrap items-center gap-4">
              <span>© {new Date().getFullYear()} Dr. Jyoti Kush. All rights reserved.</span>
              <span className="hidden md:inline opacity-30">·</span>
              <Link href="/privacy/" className="hover:text-red transition-colors">Privacy Policy</Link>
              <span className="opacity-30">·</span>
              <Link href="/terms/" className="hover:text-red transition-colors">Terms of Service</Link>
              <span className="opacity-30">·</span>
              <span className="inline-flex items-center gap-1">
                Built with{" "}
                <Heart className="size-3 text-red" fill="currentColor" />{" "}
                and discipline.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                onClick={scrollToTop}
                aria-label="Scroll to top"
                className="size-9"
              >
                <ArrowUp className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
